/* eslint-disable @next/next/no-img-element */
import React from 'react'
import { Input, Select } from 'antd'
import { AppDispatch } from 'src/app/store'
import type { TFunction } from 'next-i18next'
import { getCountryName } from 'src/core/json'
import { setProjectFormItemValue } from 'src/features'
import type { DefaultOptionType } from 'antd/lib/select'
import { BillingDataAux, Country } from 'src/core/models'
import type { SizeType } from 'antd/lib/config-provider/SizeContext'

interface BillingDataSection {
  labels: {
    title: string
    subtitle: string
  },
  form: {
    component: any // CompoundedComponent type is not exported from antd,
    children?: React.ReactNode
    props: {
      value: string | number
      size: SizeType
      placeholder: string
      onChange: Function
      showSearch?: boolean
      optionFilterProp?: string
      filterOption?: Function,
      style?: React.CSSProperties
    }
  }
}

export const billingDataSections = (
  t: TFunction, dispatch: AppDispatch, billingData: BillingDataAux, countryList: Country[]
): BillingDataSection[] => [

  { // Company Name
    labels: {
      title: t('Projects.Fields.CompanyName'),
      subtitle: t('Projects.Form.CompanyNamePlaceholder'),
    },
    form: {
      component: Input,
      props: {
        value: billingData.name,
        size: 'large',
        placeholder: t('Projects.Form.RegistryPlaceholder'),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(setProjectFormItemValue({ key: 'billing_company_name', value: e.target.value }))
        },
      }
    }
  },

  { // Fiscal ID number
    labels: {
      title: t('Projects.Fields.FiscalId'),
      subtitle: t('Projects.Form.FiscalIdPlaceHolder'),
    },
    form: {
      component: Input,
      props: {
        value: billingData.idNumber,
        size: 'large',
        placeholder: t('Projects.Form.IdNumberplaceholder'),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(setProjectFormItemValue({ key: 'billing_vat_number', value: e.target.value }))
        },
      }
    }
  },

  { // Adress
    labels: {
      title: t('Projects.Fields.Address'),
      subtitle: t('Projects.Form.AddressPlaceHolder'),
    },
    form: {
      component: Input,
      props: {
        value: billingData.address,
        size: 'large',
        placeholder: t('Projects.Form.AddressPlaceHolderTwo'),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(setProjectFormItemValue({ key: 'billing_address', value: e.target.value }))
        },
      }
    }
  },

  { // Country
    labels: {
      title: t('Projects.Fields.Country'),
      subtitle: t('Projects.Form.CountryLabel'),
    },
    form: {
      component: Select,
      props: {
        value: billingData.country,
        size: 'large',
        showSearch: true,
        style: { width: '100%' },
        placeholder: t('admin.Brokerage.CountryPlaceHolder'),
        onChange: (value: string) => {
          dispatch(setProjectFormItemValue({ key: 'billing_country', value: +value }))
        },
        filterOption: (input: string, option: DefaultOptionType) => {
          const countryLabel = (option!.children[1]?.props?.children as unknown as string)
          return countryLabel?.toLowerCase()?.includes(input?.toLowerCase())
        }
      },
      children: <>
        {countryList.map((country) => <Select.Option
          key={country.id} value={country.id}
        >
          <img
            src={country.flag}
            alt={`flag-${country?.name}`}
            style={{ width: '25px', height: '20px' }}
          />
          <span style={{ color: '#777e8b', marginLeft: '10px' }}>
            {getCountryName(country?.code2, country?.name)}
          </span>
        </Select.Option>)}
      </>
    }
  },

  { // City
    labels: {
      title: t('settings.City'),
      subtitle: t('Projects.Form.CityLabel'),
    },
    form: {
      component: Input,
      props: {
        value: billingData.city,
        size: 'large',
        placeholder: t('Projects.Form.CityPlaceHolder'),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(setProjectFormItemValue({ key: 'billing_city', value: e.target.value }))
        },
      }
    }
  },

  { // Province
    labels: {
      title: t('Projects.Fields.Province'),
      subtitle: t('Projects.Form.ProvinceLabel'),
    },
    form: {
      component: Input,
      props: {
        value: billingData.state,
        size: 'large',
        placeholder: t('Projects.Form.ProvincePlaceHolder'),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(setProjectFormItemValue({ key: 'billing_state', value: e.target.value }))
        },
      }
    }
  },

  { // Postal Code
    labels: {
      title: t('settings.PostalCode'),
      subtitle: t('Projects.Form.PostalCodeLabel'),
    },
    form: {
      component: Input,
      props: {
        value: billingData.postalCode,
        size: 'large',
        placeholder: t('Projects.Form.PostalCodePlaceHolder'),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(setProjectFormItemValue({ key: 'billing_postal_code', value: e.target.value }))
        },
      }
    }
  },
]
